<template>
  <div>
    <label for="phone" :class="phoneNumberError ? 'phone-input-label phone-input-label-error' : 'phone-input-label'">{{
      $t("auth.phone_number")
    }}</label>
    <vue-tel-input
      ref="phoneNumber"
      @validate="updatePhoneNumber"
      @blur="onPhoneNumberBlur"
      @focus="onPhoneNumberFocus"
      :inputOptions="phoneInputOptions"
      :dropdownOptions="phoneDropdownOptions"
      validCharactersOnly
      :styleClasses="phoneNumberError ? 'phone-number-input phone-number-input-error' : 'phone-number-input'">
      <template #arrow-icon>
        <v-icon size="24">mdi-chevron-down</v-icon>
      </template>
    </vue-tel-input>
    <div v-if="phoneNumberError" class="phone-error-message">
      <v-icon color="error" size="16"> mdi-alert-circle</v-icon>
      {{ phoneNumberError }}
    </div>
  </div>
</template>

<script>
  import { VueTelInput } from "vue-tel-input";
  import "vue-tel-input/dist/vue-tel-input.css";

  export default {
    name: "PhoneInput",

    components: {
      VueTelInput,
    },
    emits: ["blur"],
    props: {
      value: {
        type: String,
        default: "",
      },
      metadata: {
        type: Object,
        default: () => ({
          number: "",
          valid: false,
        }),
      },
    },
    data() {
      return {
        phoneInputOptions: {
          placeholder: "(000) 000-0000",
          name: "phone",
          maxlength: 25,
          required: true,
        },
        phoneDropdownOptions: {
          showDialCodeInSelection: true,
          showFlags: true,
          showDialCodeInList: true,
        },
        phoneObserver: null,
        phoneDropdownObserver: null,
        phoneNumberObject: {
          number: "",
          valid: false,
        },
        phoneNumberError: "",
      };
    },
    mounted() {
      const callback = mutationList => {
        for (const mutation of mutationList) {
          if (mutation.type === "attributes" && mutation.attributeName === "class") {
            if (mutation.target.classList.contains("open")) {
              const phoneDropdown = document.querySelector(".vti__dropdown-list");
              if (phoneDropdown) {
                this.handlePhoneNumberSize(phoneDropdown);
                this.phoneDropdownObserver.observe(phoneDropdown, {
                  attributes: true,
                  attributeFilter: ["style"],
                });
              }
            }
          }
        }
      };

      this.phoneObserver = new MutationObserver(callback);

      const targetNode = document.querySelector(".vti__dropdown");
      if (targetNode) {
        this.phoneObserver.observe(targetNode, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }

      const dropdownCallback = mutationList => {
        for (const mutation of mutationList) {
          if (mutation.type === "attributes" && mutation.attributeName === "style") {
            this.handlePhoneNumberSize();
          }
        }
      };

      this.phoneDropdownObserver = new MutationObserver(dropdownCallback);
      window.addEventListener("resize", this.handlePhoneNumberSize);
    },
    destroyed() {
      window.removeEventListener("resize", this.handlePhoneNumberSize);
      this.phoneObserver.disconnect();
      this.phoneDropdownObserver.disconnect();
    },
    methods: {
      validate() {
        if (!this.phoneNumberObject.valid) {
          if (!this.phoneNumberObject?.number) {
            this.phoneNumberError = this.$t("generic.validation.required");
          } else {
            this.phoneNumberError = this.$t("generic.validation.invalid_phone");
          }

          return false;
        }

        return true;
      },
      updatePhoneNumber(object) {
        this.phoneNumberObject = object;
        this.$emit("input", this.phoneNumberObject?.number);
        this.$emit("update:metadata", this.phoneNumberObject);
      },
      onPhoneNumberBlur() {
        if (!this.phoneNumberObject.valid) {
          if (!this.phoneNumberObject.number) {
            this.phoneNumberError = this.$t("generic.validation.required");
          } else {
            this.phoneNumberError = this.$t("generic.validation.invalid_phone");
          }
        } else {
          this.$emit("blur");
          this.phoneNumberError = "";
        }
      },
      onPhoneNumberFocus() {
        this.phoneNumberError = "";
      },
      handlePhoneNumberSize(el) {
        if (this.$refs.phoneNumber) {
          const width = `${this.$refs.phoneNumber.$el.offsetWidth}px`;
          if (el instanceof HTMLElement) {
            el.style.width = width;
          } else {
            const el = document.querySelector(".vti__dropdown-list");
            if (el) {
              el.style.width = width;
            }
          }
        }
      },
    },
  };
</script>
<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .phone-number-input {
    border-radius: 4px;
    border: 1px solid get-color("border");

    &:focus-within {
      border-color: get-color("primary");
      box-shadow: none;
    }

    &:hover:not(:focus-within):not(.phone-number-input-error) {
      border-color: get-color("icon");
    }

    ::v-deep {
      input {
        @include text-body-2();
        color: get-color("body");
        padding: 10px 12px;
      }

      i {
        color: get-color("icon");
        margin-left: 2px;
      }

      .vti__dropdown {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
        background-color: get-color("primary-10-hover");
        padding: 10px 12px;

        &.open {
          i {
            transform: rotate(180deg);
          }
        }
      }

      .vti__dropdown-list {
        padding: 8px 0;
        border-radius: 6px;
        border: none;
        max-height: 180px;
        box-shadow: 3px 3px 10px 0px rgba(69, 64, 78, 0.15);

        &.below {
          top: 45px;
        }
      }

      .vti__dropdown-item {
        padding: 8px 16px;
        display: flex;
        align-items: center;
        gap: 8px;

        .vti__flag {
          margin-right: 5px;
        }

        &.highlighted {
          background-color: unset;
        }

        &:hover {
          background-color: get-color("primary-10-hover");
        }

        strong,
        span {
          @include text-body-2();
        }
      }

      .vti__flag-wrapper {
        display: flex;
      }

      .vti__flag {
        transform: scale(1.2);
        margin-right: 8px;
      }
    }
  }

  .phone-input-label {
    @include text-caption-bold();
    color: get-color("subtitle");
    height: 20px;
    display: block;
  }

  .phone-error-message {
    margin-top: 8px;
    @include text-caption();
    color: get-color("error");
  }

  .phone-number-input-error {
    border-color: get-color("error");
  }

  .phone-input-label-error {
    -webkit-animation: v-shake 0.6s cubic-bezier(0.25, 0.8, 0.5, 1);
    animation: v-shake 0.6s cubic-bezier(0.25, 0.8, 0.5, 1);
  }
</style>
